/**
 * v6.38.0 (G-PG-3) — ParityNumber wrapper.
 *
 * Renders a parity claim (e.g. "40/40") with structural enforcement:
 *   - Backend feature flag OFF or hook degraded (default preview state)
 *     → renders the claim text verbatim. No visual change vs today.
 *   - Backend overall=GREEN → renders the claim + small green check chip.
 *   - Backend overall=AMBER → renders the claim + amber warning chip.
 *   - Backend overall=RED   → withholds the number behind a yellow block.
 *     The claim is structurally NOT shown until governance is resolved.
 *
 * USAGE:
 *   <ParityNumber claim="40/40" kind="scope" />
 *   <ParityNumber claim={40} kind="floor-min" />
 *
 * PLAN: /app/memory/PARITY_GOVERNANCE_GUARDRAIL_PLAN_v1.md §5.2
 */
import React from 'react';
import { CheckCircle2, AlertTriangle, ShieldAlert } from 'lucide-react';
import useParityGovernance from '../hooks/useParityGovernance';

const ParityNumber = ({ claim, kind = 'value', className = '' }) => {
  const { isEnabled, overall, report } = useParityGovernance();

  // Default (flag OFF / degraded / non-super-admin / loading):
  // render the claim verbatim. No new UI surface, no behavior change.
  if (!isEnabled) {
    return (
      <span
        className={className}
        data-testid={`parity-number-${kind}`}
      >
        {claim}
      </span>
    );
  }

  if (overall === 'RED') {
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 border border-amber-400 bg-amber-50 text-amber-900 text-xs ${className}`}
        data-testid={`parity-number-${kind}-withheld`}
        title="Parity Governance Guardrail withheld this number — see /api/bp-auditor/diagnostics/parity_governance"
      >
        <ShieldAlert className="w-3 h-3" />
        <span className="font-semibold">parity withheld</span>
      </span>
    );
  }

  if (overall === 'AMBER') {
    return (
      <span
        className={`inline-flex items-center gap-1 ${className}`}
        data-testid={`parity-number-${kind}-amber`}
      >
        <span>{claim}</span>
        <AlertTriangle
          className="w-3 h-3 text-amber-600"
          aria-label="Parity Governance amber"
          title={`Parity Governance reports ${report?.findings?.length || 0} amber finding(s)`}
        />
      </span>
    );
  }

  // GREEN — claim verified against live audit.
  return (
    <span
      className={`inline-flex items-center gap-1 ${className}`}
      data-testid={`parity-number-${kind}-verified`}
    >
      <span>{claim}</span>
      <CheckCircle2
        className="w-3 h-3 text-emerald-600"
        aria-label="Parity Governance verified"
        title="Parity number verified against live BVAF audit"
      />
    </span>
  );
};

/**
 * ParityGovernanceBanner — surfaces a top-of-section banner whenever
 * the governance check has actionable findings (RED or AMBER).
 * Renders nothing when the flag is OFF or overall is GREEN.
 */
export const ParityGovernanceBanner = () => {
  const { isEnabled, overall, report } = useParityGovernance();
  if (!isEnabled) return null;
  if (overall === 'GREEN') return null;
  const tone = overall === 'RED'
    ? 'border-red-500 bg-red-50 text-red-900'
    : 'border-amber-500 bg-amber-50 text-amber-900';
  return (
    <div
      className={`border-2 ${tone} p-4 mb-4`}
      data-testid={`parity-governance-banner-${overall.toLowerCase()}`}
    >
      <div className="flex items-center gap-2 font-bold">
        <ShieldAlert className="w-5 h-5" />
        Parity Governance Guardrail — {overall}
      </div>
      <p className="text-xs mt-1 font-mono break-all">
        {(report?.findings || []).slice(0, 3).map((f, i) => (
          <span key={i} className="block">• {f.message}</span>
        ))}
        {(report?.findings || []).length > 3 && (
          <span className="block opacity-70">
            …{report.findings.length - 3} more — see
            /api/bp-auditor/diagnostics/parity_governance
          </span>
        )}
      </p>
    </div>
  );
};

export default ParityNumber;
